import { useEffect, useState, type FormEvent } from 'react';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import { api } from './lib/api.ts';
import { boot, getState, onEvent, setSideOpen, setView, useStore } from './lib/store.ts';
import { suchPfad } from './lib/suche.ts';
import { Conversation } from './components/Conversation.tsx';
import { CrmPanel } from './components/CrmPanel.tsx';
import { Sidebar } from './components/Sidebar.tsx';
import { ThreadList } from './components/ThreadList.tsx';
import { toast, Toaster } from './components/Toast.tsx';
import { AnhangVorschau } from './components/AnhangVorschau.tsx';
import { Palette } from './components/Palette.tsx';
import { ComposePage } from './pages/ComposePage.tsx';
import { SearchPage } from './pages/SearchPage.tsx';
import { SettingsPage } from './pages/SettingsPage.tsx';
import { FlowPage } from './pages/FlowPage.tsx';
import { TelTest } from './TelTest.tsx';

function Login({ onDone }: { onDone: () => void }) {
  const [user, setUser] = useState('till');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr('');
    try {
      await api.login(user, pw);
      onDone();
    } catch {
      setErr('Anmeldung fehlgeschlagen');
    }
  };
  return (
    <form className="login" onSubmit={(e) => void submit(e)}>
      <h1>Minimusiker Mail</h1>
      <label>
        Name
        <select value={user} onChange={(e) => setUser(e.target.value)}>
          <option value="till">Till</option>
          <option value="lars">Lars</option>
          <option value="maren">Maren</option>
        </select>
      </label>
      <label>
        Passwort
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus />
      </label>
      {err && <p className="err">{err}</p>}
      <button type="submit" className="btn pri">
        Anmelden
      </button>
    </form>
  );
}

function ThreadRoute() {
  const { id } = useParams();
  return <Conversation id={Number(id)} />;
}

function Shell({ page }: { page?: 'search' | 'settings' | 'flow' }) {
  const [palette, setPalette] = useState(false);
  const { sideMini, sideOpen, crmOpen, selected } = useStore((s) => ({
    sideMini: s.sideMini,
    sideOpen: s.sideOpen,
    crmOpen: s.crmOpen,
    selected: s.selected,
  }));
  const nav = useNavigate();
  const loc = useLocation();
  // auch /search/t/:id – die Suche zeigt den Treffer rechts neben den Ergebnissen (D-071)
  const showConv = /\/t\/\d+/.test(loc.pathname);
  // Endgültig gescheiterte Befehle (z. B. Senden) sofort melden – auch, wenn der Thread nicht offen ist
  useEffect(
    () =>
      onEvent((ev) => {
        if (ev.kind !== 'command.failed') return;
        toast(
          ev.type === 'send'
            ? `Senden fehlgeschlagen${ev.subject ? `: „${ev.subject}“` : ''} – ${ev.error}`
            : `Befehl ${ev.type} fehlgeschlagen – ${ev.error}`,
        );
      }),
    [],
  );
  const compose = async () => {
    const mb =
      getState().mailboxes.find((m) => m.id === getState().mailbox) ??
      getState().mailboxes.find((m) => m.syncEnabled) ??
      getState().mailboxes[0];
    if (!mb) return;
    const d = await api.newDraft({ mailboxId: mb.id });
    void nav(`/compose/${d.id}`);
  };
  // Globale Tastenkürzel: j/k Liste, c verfassen, / Suche in der Liste, ⌘F Suchseite, Esc zurück
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const typing =
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) ||
        (e.target as HTMLElement)?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette((p) => !p);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        void nav(suchPfad());
        return;
      }
      if (typing) {
        if (e.key === 'Escape') (e.target as HTMLElement).blur();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'c') void compose();
      if (e.key === '/') {
        e.preventDefault();
        document.getElementById('listSearch')?.focus();
      }
      // Auf der Suchseite: Esc schließt erst den Treffer, dann die Suche; j/k gehen durch die Treffer (SearchPage)
      const inSuche = window.location.pathname.startsWith('/search');
      if (e.key === 'Escape')
        void nav(inSuche && /\/t\/\d+/.test(window.location.pathname) ? '/search' : '/');
      if (inSuche) return;
      if (e.key === 'j' || e.key === 'k') {
        const l = getState().threads;
        const i = l.findIndex((t) => t.id === getState().selected);
        const n = e.key === 'j' ? l[Math.min(i + 1, l.length - 1)] : l[Math.max(i - 1, 0)];
        if (n) void nav(`/t/${n.id}`);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);
  const cls = [
    'app',
    sideMini ? 'side-mini' : '',
    !crmOpen && !page ? 'no-crm' : '',
    crmOpen ? 'crm-open' : '',
    showConv ? 'show-conv' : '',
    sideOpen ? 'show-side' : '',
    page ? 'page' : '',
    page === 'search' && showConv ? 'suche-offen' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls} id="app">
      {sideOpen && <div className="backdrop" onClick={() => setSideOpen(false)} />}
      {palette && <Palette onClose={() => setPalette(false)} />}
      <Sidebar onCompose={() => void compose()} onBob={() => setPalette(true)} />
      {page === 'search' ? (
        <SearchPage />
      ) : page === 'settings' ? (
        <SettingsPage />
      ) : page === 'flow' ? (
        <FlowPage />
      ) : (
        <>
          <ThreadList />
          <Routes>
            <Route path="/t/:id" element={<ThreadRoute />} />
            <Route path="*" element={<section className="conv" aria-label="Konversation" />} />
          </Routes>
          <CrmPanel threadId={showConv ? selected : null} />
        </>
      )}
    </div>
  );
}

export function App() {
  const auth = useStore((s) => s.auth);
  const nav = useNavigate();
  useEffect(() => void boot(), []);
  useEffect(() => {
    // Mobil: zurück zur Liste zeigt wieder die Seitenleiste nicht
    setSideOpen(false);
  }, [auth]);
  if (auth === 'laden') return null;
  if (auth === 'login')
    return (
      <Login
        onDone={() => {
          void boot();
          setView('inbox');
          void nav('/');
        }}
      />
    );
  return (
    <>
      <Routes>
        <Route path="/dev/tel" element={<TelTest />} />
        <Route path="/compose/:draftId" element={<ComposePage />} />
        <Route path="/search" element={<Shell page="search" />} />
        <Route path="/search/t/:id" element={<Shell page="search" />} />
        <Route path="/einstellungen" element={<Shell page="settings" />} />
        <Route path="/flow" element={<Shell page="flow" />} />
        <Route path="*" element={<Shell />} />
      </Routes>
      <Toaster />
      <AnhangVorschau />
    </>
  );
}
